import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import {
  ShieldCheck,
  IndianRupee,
  HeartHandshake,
  Clock,
  Phone,
  ArrowRight,
  Stethoscope,
  MapPin,
  Star,
  ClipboardList,
  UserCheck,
  Hospital,
  Leaf,
  Quote,
  Baby,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ComponentType, type SVGProps } from "react";
import {
  PilesIcon,
  OrthoIcon,
  NormalDeliveryIcon,
  HerniaIcon,
  GallbladderIcon,
  CSectionIcon,
  SeptoplastyIcon,
} from "@/components/HomeSurgeryIcons";


import { locations, stats, contact } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { listPublishedDoctors } from "@/lib/doctors.functions";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { DoctorPortrait } from "@/components/DoctorPortrait";
import heroImg from "@/assets/healic-hero.jpg";
import heroVideo from "@/assets/agra-healic-hospital.mp4.asset.json";
import surgeryCSection from "@/assets/surgery-c-section.jpg";
import surgeryNormalDelivery from "@/assets/home/normal-delivery.jpeg.asset.json";
import surgeryHernia from "@/assets/surgery-hernia.jpg";
import surgerySeptoplasty from "@/assets/category-hero/septoplasty.jpeg.asset.json";
import surgeryCircumcision from "@/assets/category-hero/circumcision.jpg.asset.json";
import surgeryGallbladder from "@/assets/surgery-gallbladder.jpg";
import surgeryPiles from "@/assets/home/piles.jpeg.asset.json";
import surgeryKnee from "@/assets/home/knee-replacement.jpeg.asset.json";
import hospitalGurugram from "@/assets/hospital-gurugram.webp";
import hospitalGhaziabad from "@/assets/hospital-ghaziabad.webp";
import hospitalHaridwar from "@/assets/hospital-haridwar.webp";
import hospitalAgra from "@/assets/hospital-agra.webp";
import hospitalExterior from "@/assets/hospital-exterior.jpg";

const locationImages: Record<string, string> = {
  "sohna-road-gurugram": hospitalGurugram,
  "indirapuram-ghaziabad": hospitalGhaziabad,
  "haridwar": hospitalHaridwar,
  "agra": hospitalAgra,
  "muzaffarnagar": hospitalExterior,
};


type IconComp = LucideIcon | ComponentType<{ className?: string } & SVGProps<SVGSVGElement>> | ComponentType<{ className?: string }>;

const commonSurgeries: { slug: string; name: string; short: string; image: string; icon: IconComp }[] = [
  { slug: "normal-c-section-delivery", name: "C-Section Delivery", short: "Safe, planned caesarean with senior gynaecologists.", image: surgeryCSection, icon: Baby },
  { slug: "normal-c-section-delivery", name: "Normal Delivery", short: "Natural birthing with round-the-clock OBGY care.", image: surgeryNormalDelivery.url, icon: NormalDeliveryIcon },
  { slug: "hernia-repair", name: "Hernia Repair", short: "Laparoscopic mesh repair, day-care discharge.", image: surgeryHernia, icon: HerniaIcon },
  { slug: "septoplasty", name: "Septoplasty", short: "Endoscopic correction of deviated nasal septum.", image: surgerySeptoplasty.url, icon: SeptoplastyIcon },
  { slug: "circumcision", name: "Circumcision", short: "ZSR stapler & laser — stitch-free, daycare.", image: surgeryCircumcision.url, icon: CSectionIcon },
  { slug: "gallbladder-removal", name: "Gallbladder Stone Removal", short: "Keyhole cholecystectomy, fast recovery.", image: surgeryGallbladder, icon: GallbladderIcon },
  { slug: "piles-hemorrhoidectomy", name: "Piles Treatment", short: "Painless laser & stapler procedures.", image: surgeryPiles.url, icon: PilesIcon },
  { slug: "knee-replacement", name: "Knee Replacement", short: "Total knee replacement with rapid rehab.", image: surgeryKnee.url, icon: OrthoIcon },
];



export const Route = createFileRoute("/")({
  loader: async () => {
    try {
      return await listPublishedDoctors();
    } catch (error) {
      console.error("[home-page] Falling back to the local doctor directory", error);
      return [];
    }
  },
  head: () => ({
    meta: [
      { title: "Healic Multispeciality Hospital — Planned Surgery" },
      { name: "description", content: "Fixed-price planned surgery with expert surgeons across Gurugram, Ghaziabad, Haridwar, Agra and Muzaffarnagar. No billing surprises. Book your appointment today." },
      { property: "og:title", content: "Healic — Fixed-Price Planned Surgery" },
      { property: "og:description", content: "Parivar jaisi dekhbhal — expert surgeons, transparent pricing, modern hospitals." },
      { property: "og:url", content: "https://healic.in/" },
    ],
    links: [
      { rel: "canonical", href: "https://healic.in/" },
      { rel: "preload", as: "image", href: heroImg, fetchPriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How Healic Surgery Works",
          description: "Four clear steps from Consultation To recovery with Healic.",
          totalTime: "P7D",
          step: [
            {
              "@type": "HowToStep",
              position: 1,
              name: "Book Free Consultation",
              text: "Call, WhatsApp, or fill the form. We'll explain your procedure and package clearly.",
            },
            {
              "@type": "HowToStep",
              position: 2,
              name: "Meet Your Surgeon",
              text: "You meet the actual surgeon who will operate on you. At your Healic hospital.",
            },
            {
              "@type": "HowToStep",
              position: 3,
              name: "Surgery at Our Hospital",
              text: "Same surgeon. Our OT. On the date that works for you. Everything covered in your package.",
            },
            {
              "@type": "HowToStep",
              position: 4,
              name: "Recovery + Follow-up",
              text: "Two follow-up consultations included. WhatsApp support throughout recovery.",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "Review",
            itemReviewed: {
              "@type": "MedicalOrganization",
              name: "Healic",
            },
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
            },
            author: {
              "@type": "Person",
              name: "Rajesh Sharma",
            },
            reviewBody:
              "Mujhe pehle se package bata diya tha — ₹42,000 mein sab kuch. Koi extra charge nahi aaya. Doctor se pehle mili, surgery ke din wahi the. Ek hafte mein kaam pe wapas aa gaya.",
            publisher: {
              "@type": "Organization",
              name: "Healic",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Review",
            itemReviewed: {
              "@type": "MedicalOrganization",
              name: "Healic",
            },
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
            },
            author: {
              "@type": "Person",
              name: "Priya Gupta",
            },
            reviewBody:
              "Delivery package mein sab kuch tha — tests se lekar baby care tak. Koi surprise bill nahi. Doctors bahut achhe the. Main apni behen ko bhi Healic bhejungi.",
            publisher: {
              "@type": "Organization",
              name: "Healic",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Review",
            itemReviewed: {
              "@type": "MedicalOrganization",
              name: "Healic",
            },
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
            },
            author: {
              "@type": "Person",
              name: "Amit Verma",
            },
            reviewBody:
              "Mera 67 saal ka pita ji — 3 saal se dard mein the. Healic mein EMI pe knee replacement hua. Ab woh akele chal lete hain. Package mein sab tha, koi hidden charge nahi.",
            publisher: {
              "@type": "Organization",
              name: "Healic",
            },
          },
        ]),
      },
    ],
  }),
  component: HomePage,
});

const trustPoints = [
  { icon: IndianRupee, title: "Fixed prices", body: "Surgery cost agreed upfront — no billing surprises." },
  { icon: ShieldCheck, title: "Expert surgeons", body: "100+ specialists with deep procedural experience." },
  { icon: HeartHandshake, title: "Parivar jaisi dekhbhal", body: "Compassionate care from admission to recovery." },
  { icon: Clock, title: "Single Point of Customer support", body: "Care helpline, ICU and emergency cover round the clock." },
];

function HomePage() {
  const initialDoctors = Route.useLoaderData();
  const allDoctors = useMergedDoctors(false, initialDoctors);
  const featuredDoctors = [...allDoctors].sort((a, b) => {
    const ax = parseInt(String(a.experience ?? "").match(/\d+/)?.[0] ?? "0", 10);
    const bx = parseInt(String(b.experience ?? "").match(/\d+/)?.[0] ?? "0", 10);
    return bx - ax;
  });
  // Defer the hero video until after the LCP paints so it doesn't compete
  // for bandwidth with the hero image and above-the-fold text. Skip it
  // entirely on small screens, data-saver mode and slow connections.
  const [showVideo, setShowVideo] = useState(false);
  useEffect(() => {
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const conn = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    const slow =
      conn?.saveData === true ||
      (conn?.effectiveType ? /2g|slow-2g|3g/.test(conn.effectiveType) : false);
    if (slow || window.matchMedia("(max-width: 1023px)").matches) return;
    const schedule = win.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
    const handle = schedule(() => setShowVideo(true), { timeout: 2500 });
    return () => {
      if (typeof handle === "number") window.clearTimeout(handle);
    };
  }, []);

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <img
          src={heroImg}
          alt=""
          aria-hidden
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover opacity-90"
        />
        {showVideo && (
          <video
            className="absolute inset-0 h-full w-full object-cover opacity-90"
            src={heroVideo.url}
            poster={heroImg}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/55 to-primary/20" aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-12 lg:grid-cols-[1.2fr_1fr] lg:px-8 lg:py-16">
          <div>
            <span className="inline-flex items-center gap-2 rounded-pill bg-accent/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
              <Star className="h-3 w-3 fill-accent" /> TRUSTED BY 1 LAKH+ PATIENTS
            </span>
            <h1 className="mt-4 font-display text-3xl font-bold leading-[1.1] sm:text-4xl lg:text-5xl">
              Planned Surgery At Healic —{" "}
              <span className="italic text-accent">Tension-Free, Parivaar Jaisi Dekhbhal</span>
            </h1>
            <p className="mt-4 max-w-xl text-base font-semibold opacity-90 lg:text-lg">
              India's First Planned Surgery Hospital Chain
            </p>

            <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
              <Link
                to="/contact"
                className="hero-cta inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill bg-accent px-4 py-2.5 text-xs font-semibold text-accent-foreground shadow-cta hover:opacity-90 sm:px-6 sm:py-3 sm:text-sm"
              >
                Request Call Back <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/specialties"
                className="hero-cta inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill bg-white/10 px-4 py-2.5 text-xs font-semibold ring-1 ring-white/20 hover:bg-white/15 sm:px-6 sm:py-3 sm:text-sm"
              >
                <Stethoscope className="h-4 w-4" /> Explore surgeries
              </Link>
              <a
                href={`tel:${contact.phoneTel}`}
                className="hero-cta inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill bg-white px-4 py-2.5 text-xs font-bold text-primary shadow-sm hover:bg-white/90 sm:px-6 sm:py-3 sm:text-sm"
              >
                <Phone className="h-4 w-4" /> {contact.phone}
              </a>
            </div>

          </div>

          {/* Trust card */}
          <div className="rounded-3xl bg-background p-4 text-foreground shadow-card sm:p-6 lg:p-8">
            <h2 className="font-display text-lg font-semibold">Why families choose Healic</h2>
            <div className="mt-4 grid grid-cols-2 gap-2.5 sm:mt-5 sm:gap-4">
              {trustPoints.map((p) => (
                <div key={p.title} className="rounded-2xl bg-cream p-3 sm:p-4">
                  <p.icon className="h-5 w-5 text-accent" />
                  <p className="mt-2 text-xs font-semibold sm:mt-3 sm:text-sm">{p.title}</p>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground sm:text-xs">{p.body}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Most common surgeries */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">What we treat</p>
            <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">
              Our Most Commonly Done <span className="text-accent">Surgeries</span>
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Maternity, general surgery, ophthalmology and joint care — the procedures families trust Healic with every day.
            </p>
          </div>
          <Link to="/surgeries" className="text-sm font-semibold text-primary hover:text-accent">
            View all surgeries →
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
          {commonSurgeries.map((s) => {
            const Icon = s.icon;
            return (
            <Link
              key={s.name}
              to="/surgeries/$slug"
              params={{ slug: s.slug }}
              className="group overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:border-accent hover:shadow-card"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  width={768}
                  height={576}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-accent-soft text-primary sm:h-10 sm:w-10">
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <h3 className="mt-2 font-display text-sm font-semibold leading-snug sm:mt-3 sm:text-base">{s.name}</h3>
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground sm:text-xs">{s.short}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary group-hover:text-accent sm:mt-3 sm:text-xs">
                  Know more <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
            );
          })}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-cream">
        <div className="mobile-stats-grid mx-auto grid max-w-7xl grid-cols-2 gap-3 px-4 py-10 sm:gap-6 lg:grid-cols-4 lg:px-8">
          {stats.map((s) => (
            <div key={s.label} className="mobile-stat-card rounded-3xl bg-background p-4 shadow-soft sm:p-6">
              <div className="font-display text-2xl font-bold text-primary sm:text-4xl">{s.value}</div>
              <p className="mt-1.5 text-xs font-medium sm:mt-2 sm:text-sm">{s.label}</p>
              {s.note && <p className="mt-1 text-[11px] text-muted-foreground sm:text-xs">{s.note}</p>}
            </div>

          ))}
        </div>
      </section>

      {/* Doctors preview */}
      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Our team</p>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">Top Specialists For You</h2>
          </div>
          <Link to="/doctors" className="text-sm font-semibold text-primary hover:text-accent">
            View all doctors →
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {featuredDoctors.slice(0, 4).map((d) => {
            const profileSlug = doctorProfileSlug(d.slug, d.name);
            return (
              <Link
                key={d.slug}
                to="/doctors/$slug"
                params={{ slug: profileSlug }}
                className="group overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:border-accent hover:shadow-card"
              >
                <DoctorPortrait
                  doctor={d}
                  className="aspect-[4/3] w-full overflow-hidden"
                  imgClassName="transition duration-500 group-hover:scale-105"
                />
                <div className="p-3 sm:p-4">
                  <h3 className="font-display text-sm font-semibold leading-snug sm:text-base">{d.name}</h3>
                  <p className="mt-1 text-[11px] text-accent sm:text-xs">{d.specialty}</p>
                  {d.experience && <p className="mt-1 text-[11px] text-muted-foreground sm:text-xs">{d.experience} experience</p>}
                  {d.hospital && <p className="text-[11px] text-muted-foreground sm:text-xs">Healic — {d.hospital}</p>}
                </div>

              </Link>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8 lg:py-10">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">How it works</p>
            <h2 className="mt-1 font-display text-xl font-bold sm:text-2xl">
              From Consultation To{" "}
              <span className="text-accent">
                {"\u00a0"}Recovery{"\u00a0"}
              </span>
            </h2>
            <p className="mx-auto mt-1 max-w-2xl text-xs text-muted-foreground">
              Four clear steps. One consistent team. Everything included in your package.
            </p>
          </div>

          <div className="relative mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="pointer-events-none absolute left-0 right-0 top-7 hidden h-px bg-border lg:block" aria-hidden />
            {[
              { icon: ClipboardList, num: 1, title: "Book Free Consultation", body: "Call, WhatsApp, or fill the form. We'll explain your procedure and package clearly." },
              { icon: UserCheck, num: 2, title: "Meet Your Surgeon", body: "You meet the actual surgeon who will operate on you. At your Healic hospital." },
              { icon: Hospital, num: 3, title: "Surgery at Our Hospital", body: "Same surgeon. Our OT. On the date that works for you. Everything covered in your package." },
              { icon: Leaf, num: 4, title: "Recovery + Follow-up", body: "Two follow-up consultations included. WhatsApp support throughout recovery." },
            ].map((step) => (
              <div key={step.num} className="relative flex flex-col items-center text-center">
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-background shadow-soft">
                    <step.icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground shadow-cta">
                    {step.num}
                  </span>
                </div>
                <h3 className="mt-2 font-display text-sm font-semibold">{step.title}</h3>
                <p className="mt-1 max-w-xs text-xs text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <Link
              to="/contact"
              className="rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-cta hover:opacity-90"
            >
              Book your free consultation
            </Link>
          </div>
        </div>
      </section>


      {/* Locations */}
      <section className="mx-auto max-w-7xl px-4 py-8 lg:px-8 lg:py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Locations</p>
            <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">Find us in your city</h2>
          </div>
          <Link to="/locations" className="text-sm font-semibold text-primary hover:text-accent">
            All hospitals →
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {locations.map((l) => (
            <Link
              key={l.slug}
              to={locationHref(l.slug)}
              className="group overflow-hidden rounded-2xl bg-background shadow-soft transition hover:-translate-y-1 hover:shadow-card"
            >
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={locationImages[l.slug]}
                  alt={`Healic Hospital ${l.city}`}
                  width={768}
                  height={480}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              </div>
              <div className="p-3 sm:p-4">
                <MapPin className="h-4 w-4 text-accent" />
                <h3 className="mt-1.5 font-display text-sm font-semibold sm:mt-2">{l.city}</h3>
                <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground sm:text-xs">{l.address}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>


      {/* Patient stories */}
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Patient stories</p>
            <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">
              What Our Patients{" "}
              <span className="text-accent">
                say
              </span>
            </h2>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                tag: "Hernia Surgery — Gurugram",
                quote: "Mujhe pehle se package bata diya tha — ₹42,000 mein sab kuch. Koi extra charge nahi aaya. Doctor se pehle mili, surgery ke din wahi the. Ek hafte mein kaam pe wapas aa gaya.",
                name: "Rajesh Sharma",
                meta: "48 yrs · Sector 56, Gurugram",
                initials: "RS",
              },
              {
                tag: "Normal Delivery — Agra",
                quote: "Delivery package mein sab kuch tha — tests se lekar baby care tak. Koi surprise bill nahi. Doctors bahut achhe the. Main apni behen ko bhi Healic bhejungi.",
                name: "Priya Gupta",
                meta: "28 yrs · Civil Lines, Agra",
                initials: "PG",
              },
              {
                tag: "Knee Replacement — Gurugram",
                quote: "Mera 67 saal ka pita ji — 3 saal se dard mein the. Healic mein EMI pe knee replacement hua. Ab woh akele chal lete hain. Package mein sab tha, koi hidden charge nahi.",
                name: "Amit Verma",
                meta: "42 yrs · Badshahpur, Gurugram",
                initials: "AV",
              },
            ].map((t) => (
              <article key={t.name} className="flex flex-col rounded-3xl bg-background p-5 shadow-soft">
                <Quote className="h-5 w-5 text-accent" />
                <span className="mt-3 inline-flex w-fit rounded-pill bg-accent-soft px-2.5 py-0.5 text-[11px] font-semibold text-primary">
                  {t.tag}
                </span>
                <p className="mt-3 flex-1 font-display text-sm italic leading-relaxed text-foreground/90">
                  {t.quote}
                </p>
                <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-primary">
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-[11px] text-muted-foreground">{t.meta}</p>
                    <div className="mt-0.5 flex gap-0.5 text-accent">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-accent" />
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}

      <section className="mx-auto max-w-7xl px-4 pb-2 pt-4 lg:px-8 lg:pb-3 lg:pt-6">
        <div className="overflow-hidden rounded-3xl bg-cta-gradient p-6 text-white shadow-card lg:p-8">
          <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div>
              <h2 className="font-display text-2xl font-bold sm:text-3xl">
                Still Unsure About Treatment?
              </h2>
              <p className="mt-1.5 max-w-xl text-sm opacity-85">
                Our care team will guide you through diagnosis, treatment options and recovery.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5 lg:justify-end">
              <Link
                to="/contact"
                className="rounded-pill bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow-cta hover:opacity-90"
              >
                Get expert advice
              </Link>
              <a
                href={`https://wa.me/${contact.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-pill bg-white/10 px-5 py-2.5 text-sm font-semibold ring-1 ring-white/20 hover:bg-white/15"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
